import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./components/ui/dialog";
import { AlertTriangle } from "lucide-react";

const heat = (rate) => {
  if (rate >= 90) return "bg-emerald-600 text-white";
  if (rate >= 75) return "bg-emerald-400 text-white";
  if (rate >= 60) return "bg-emerald-200 text-emerald-900";
  if (rate >= 40) return "bg-amber-200 text-amber-900";
  return "bg-red-200 text-red-900";
};

const Toggle = ({ value, onChange }) => (
  <div className="inline-flex rounded-md border border-input p-0.5 text-xs" data-testid="cohort-granularity">
    {[["month", "Mensile"], ["quarter", "Trimestrale"]].map(([v, l]) => (
      <button key={v} onClick={() => onChange(v)} data-testid={`cohort-granularity-${v}`}
        className={`px-2.5 py-1 rounded transition-colors ${value === v ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>{l}</button>
    ))}
  </div>
);

const STATUS_IT = { cancelled: "Annullato", frozen: "Congelato", refunded: "Rimborsato" };
const itDate = (s) => (s ? new Date(s).toLocaleDateString("it-IT") : "—");

const Cell = ({ cell, initial, onClick, testId }) => cell
  ? <td className={`px-3 py-2 text-center font-medium cursor-pointer hover:ring-2 hover:ring-primary/60 hover:ring-inset transition-shadow ${heat(cell.rate)}`} title={`${cell.retained} / ${initial} clienti attivi — clicca per il dettaglio`} onClick={onClick} data-testid={testId}>{cell.rate.toLocaleString("it-IT")}%</td>
  : <td className="px-3 py-2 text-center text-muted-foreground bg-muted/30">—</td>;

const ClientList = ({ title, items, lost, testId }) => (
  <div data-testid={testId}>
    <p className={`text-xs font-semibold uppercase mb-1.5 ${lost ? "text-red-600" : "text-emerald-700"}`}>{title} ({items.length})</p>
    {items.length === 0 ? <p className="text-xs text-muted-foreground">Nessuno</p> : (
      <div className="border rounded-md divide-y max-h-64 overflow-y-auto">
        {items.map(c => (
          <div key={c.client_id} className="px-3 py-1.5 flex items-center justify-between text-sm">
            <span>{c.name}</span>
            <span className="text-xs text-muted-foreground">
              {lost ? `${STATUS_IT[c.status] || c.status} il ${itDate(c.lost_date)}` : `attivo dal ${itDate(c.first_paid)}`}
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
);

const CellDetail = ({ detail, onClose }) => (
  <Dialog open={!!detail} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-lg" data-testid="cohort-detail-dialog">
      {detail && <>
        <DialogHeader>
          <DialogTitle>{detail.row.label} · dopo {detail.m} {detail.m === 1 ? "mese" : "mesi"}</DialogTitle>
          <p className="text-sm text-muted-foreground" data-testid="cohort-detail-summary">
            <b className="text-red-600">{detail.cell.lost_clients.length} persi</b> su {detail.row.initial} clienti iniziali · {detail.cell.retained} ancora attivi ({detail.cell.rate.toLocaleString("it-IT")}%)
          </p>
        </DialogHeader>
        <div className="space-y-4">
          <ClientList title="Clienti persi" items={detail.cell.lost_clients} lost testId="cohort-detail-lost" />
          <ClientList title="Clienti ancora attivi" items={detail.cell.retained_clients} testId="cohort-detail-retained" />
        </div>
      </>}
    </DialogContent>
  </Dialog>
);

export const CohortRetention = ({ cohorts }) => {
  const [g, setG] = useState("quarter");
  const [detail, setDetail] = useState(null);
  const d = (cohorts || {})[g];
  if (!d) return null;
  const suspect = d.rows.reduce((s, r) => s + r.suspect_clients, 0);
  return (
    <Card className="border-border/60" data-testid="cohort-retention-widget">
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-base">Customer Cohort Retention</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">% di clienti della coorte ancora attivi e paganti dopo N mesi dalla prima attivazione a pagamento. Un cliente = un'organizzazione; stato alla data (i riattivati tornano a contare).</p>
        </div>
        <Toggle value={g} onChange={setG} />
      </CardHeader>
      <CardContent>
        {d.rows.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">Nessun dato</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-separate border-spacing-0.5" data-testid="cohort-table">
              <thead><tr className="text-xs text-muted-foreground">
                <th className="px-3 py-2 text-left font-medium">Coorte</th>
                <th className="px-3 py-2 text-right font-medium">Clienti iniziali</th>
                {d.offsets.map(m => <th key={m} className="px-3 py-2 text-center font-medium">{m} {m === 1 ? "mese" : "mesi"}</th>)}
              </tr></thead>
              <tbody>{d.rows.map(r => (
                <tr key={r.cohort} data-testid={`cohort-row-${r.cohort}`}>
                  <td className="px-3 py-2 font-medium whitespace-nowrap">{r.label}{r.suspect_clients > 0 && <span title={`${r.suspect_clients} clienti con date di cessazione incomplete`} data-testid={`cohort-warning-${r.cohort}`}><AlertTriangle className="inline h-3.5 w-3.5 ml-1 text-amber-500" /></span>}</td>
                  <td className="px-3 py-2 text-right">{r.initial}</td>
                  {d.offsets.map(m => <Cell key={m} cell={r.cells[String(m)]} initial={r.initial} testId={`cohort-cell-${r.cohort}-${m}`}
                    onClick={() => setDetail({ row: r, m, cell: r.cells[String(m)] })} />)}
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
        <div className="mt-3 space-y-1 text-xs text-muted-foreground" data-testid="cohort-notes">
          <p>"—" = periodo non ancora osservabile per tutti i clienti della coorte. Esclusi contratti a importo zero ({d.contracts_free}). I contratti scaduti e non rinnovati risultano ancora attivi finché non viene gestito il rinnovo.</p>
          {suspect > 0 && <p className="text-amber-600" data-testid="cohort-data-warning"><AlertTriangle className="inline h-3.5 w-3.5 mr-1" />{suspect} clienti hanno contratti persi senza data di cessazione o con cessazione uguale all'inizio: i valori di quelle coorti possono essere inaffidabili (vedi Verifica Churn).</p>}
          {d.contracts_without_start > 0 && <p className="text-amber-600">{d.contracts_without_start} contratti senza data di inizio non sono inclusi.</p>}
        </div>
      </CardContent>
      <CellDetail detail={detail} onClose={() => setDetail(null)} />
    </Card>
  );
};
